import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import { FaTimes } from 'react-icons/fa';
import { IP } from "../configuracion.js";
import "../styles/PromoSelector.css";

// PromoSelector
//
// Modal del botón "Promo" (catálogo lista_promos.json). Totalmente
// independiente de OpenDiscountModal.js / DiscountModal (botones
// "Descuento" y "Descuento Abierto"): no importa ni reutiliza su código,
// y llama a un endpoint backend propio (aplicar_promo.php), para no
// afectar de ninguna forma el funcionamiento actual de esos botones.
//
// Organiza las promociones en pestañas dinámicas según su "tipo"
// (PORCENTAJE -> Descuentos %, PRECIO -> Descuentos $, PROMOCION ->
// Promociones), mostrando solo las activas y válidas para el Revenue en el
// que se está operando actualmente.
const TAB_LABELS = {
  PORCENTAJE: "Descuentos %",
  PRECIO: "Descuentos $",
  PROMOCION: "Promociones",
};

const TAB_ORDER = ["PORCENTAJE", "PRECIO", "PROMOCION"];

const PromoSelector = ({
  property,
  promos,
  order,
  selectedRevenue,
  selectedSpace,
  chequeSeleccionado,
  currentUser,
  // Requerimiento: si el mesero seleccionó uno o varios productos del
  // ticket antes de abrir "Promo", la promoción debe aplicarse solo a
  // esos productos, no a todo el pedido. selectedIndices llega desde
  // App.js (misma fuente que ya usa el botón "Eliminar" para
  // multi-selección: selectedOrderIndices, o el índice único si solo hay
  // uno seleccionado). Si viene vacío, se conserva el comportamiento
  // actual (promo sobre el pedido completo).
  selectedIndices = [],
  onApplyComplete,
  onClose,
}) => {
  const revenueId = selectedRevenue?.id;

  // Requerimiento 6: solo promociones activas y configuradas para el
  // Revenue donde se está trabajando actualmente.
  const promosVisibles = useMemo(() => {
    if (!Array.isArray(promos)) return [];
    return promos.filter((p) => {
      if (p.activo === false) return false;
      if (!Array.isArray(p.revenues) || p.revenues.length === 0) return true;
      return revenueId != null && p.revenues.some((r) => Number(r) === Number(revenueId));
    });
  }, [promos, revenueId]);

  // Requerimiento 5: pestañas dinámicas — solo se muestran los tipos
  // que realmente existen en el catálogo filtrado.
  const availableTabs = useMemo(() => {
    const types = new Set(promosVisibles.map((p) => p.tipo));
    return TAB_ORDER.filter((t) => types.has(t));
  }, [promosVisibles]);

  const [activeTab, setActiveTab] = useState(null);
  const currentTab = activeTab && availableTabs.includes(activeTab) ? activeTab : availableTabs[0];

  const [applying, setApplying] = useState(false);
  const [error, setError] = useState(null);

  const getItemSubtotal = (item) => {
    const itemPrice = item.price || 0;
    const modifiersTotal = item.modifiers
      ? item.modifiers.reduce((modTotal, mod) => modTotal + (mod.price * (mod.quantity || 1)), 0)
      : 0;
    return (itemPrice + modifiersTotal) * (item.quantity || 1);
  };

  // Productos realmente seleccionados (ya guardados en el cheque — no
  // tiene sentido aplicar una promo a un renglón que todavía no existe en
  // BD). Se guarda { index, item } porque para poder actualizar el
  // ticket de inmediato (ver handleSelectPromoAProductos) hace falta
  // saber la posición original en "order", no solo el producto.
  // Si no hay selección, selectedItems queda vacío y todo el flujo se
  // comporta exactamente como antes (promo sobre el pedido completo).
  const selectedItems = useMemo(() => {
    if (!Array.isArray(selectedIndices) || selectedIndices.length === 0) return [];
    return selectedIndices
      .map((i) => ({ index: i, item: order?.[i] }))
      .filter((x) => x.item && x.item.isFromDB);
  }, [selectedIndices, order]);

  const hasSelection = selectedItems.length > 0;

  const orderTotal = useMemo(() => {
    const source = hasSelection ? selectedItems.map((x) => x.item) : (order || []);
    return source.reduce((total, item) => total + getItemSubtotal(item), 0);
  }, [order, selectedItems, hasSelection]);

  // Requerimiento 7: toda la lógica de aplicación viene del JSON. El
  // usuario solo elige el nombre de la promoción; nunca captura
  // porcentajes ni importes.
  // baseAmount es opcional y por defecto usa orderTotal, que ya viene
  // acotado al subtotal combinado de la selección cuando hasSelection es
  // true (ver el useMemo de orderTotal arriba) — no hace falta pasarlo
  // explícitamente en ningún caso actual, el parámetro solo queda
  // disponible por si a futuro se necesita calcular sobre otra base.
  const calcularMontoDescuento = (promo, baseAmount = orderTotal) => {
    if (promo.tipo === "PORCENTAJE") {
      return baseAmount * (Number(promo.valor) / 100);
    }
    if (promo.tipo === "PRECIO") {
      return Math.min(Number(promo.valor), baseAmount);
    }
    if (promo.tipo === "PROMOCION") {
      // 2x1, 3x2, 4x3, etc. ya vienen resueltos como un porcentaje
      // equivalente en el JSON (porcentaje_equivalente), calculado por
      // quien administra el catálogo — aquí solo se aplica.
      const pctEquivalente = Number(promo.porcentaje_equivalente) || 0;
      return baseAmount * (pctEquivalente / 100);
    }
    return null;
  };

  const mesa = selectedRevenue?.Tipo === "Retail" ? "Retail 1" : (selectedSpace?.name || "Sin especificar");
  const empleado = currentUser?.nombre || "Sistema";

  const postJson = async (url, payload) => {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Property": property,
      },
      body: JSON.stringify(payload),
    });

    const responseText = await response.text();
    if (!response.ok) {
      throw new Error(`Error HTTP ${response.status}: ${responseText}`);
    }

    let data;
    try {
      data = JSON.parse(responseText);
    } catch (e) {
      throw new Error("Respuesta inválida del servidor");
    }

    if (!data.success) {
      throw new Error(data.message || "Error al aplicar la promoción");
    }
    return data;
  };

  const postAplicarPromo = (payload) =>
    postJson(`${IP}/tavpos/backend/aplicar_promo.php?property=${encodeURIComponent(property)}`, payload);

  // aplicar_descuento.php — el mismo endpoint que ya usa el botón
  // "Descuentos" (catálogo) para descontar UN producto específico sin
  // afectar a los demás (ver processExpressDiscounts en App.js).
  const postAplicarDescuentoPorArticulo = (payload) =>
    postJson(`${IP}/tavpos/backend/aplicar_descuento.php?property=${encodeURIComponent(property)}`, payload);

  // Requerimiento (segunda vuelta): la primera versión mandaba la promo a
  // aplicar_promo.php con un campo "articuloId" nuevo, asumiendo que el
  // backend lo usaría para acotar el descuento a un producto. Eduardo
  // probó en el sistema real y confirmó que seguía aplicándose a toda la
  // cuenta — aplicar_promo.php ignora ese campo, cambiarlo desde el
  // frontend no alcanza porque el backend nunca lo lee.
  //
  // Se confirmó contra datos reales de producción (lectura_cheques.php)
  // que SÍ existe un mecanismo que persiste un descuento en un producto
  // específico sin tocar los demás: cada producto trae sus propios
  // campos nombre_descuento/monto_descuento, y se encontró un cheque real
  // donde solo UN producto tenía "nombre_descuento":"30% EMPLEADOS"
  // mientras los productos vecinos del mismo cheque tenían
  // "monto_descuento":0. Ese es el mecanismo que ya usa el botón
  // "Descuentos" (catálogo) vía aplicar_descuento.php + articuloId. En
  // vez de seguir insistiendo con aplicar_promo.php, la promo
  // seleccionada ahora reutiliza ESE endpoint ya comprobado en
  // producción: una llamada por producto seleccionado, cada una dirigida
  // a su propio articuloId (no hay riesgo de duplicar/acumular al
  // seleccionar varios, porque cada llamada ya apunta a un solo renglón).
  //
  // aplicar_descuento.php trabaja con porcentaje, no con monto en $, así
  // que las promos tipo PRECIO/PROMOCION se convierten a un % equivalente
  // sobre el subtotal de CADA producto antes de enviarlas.
  const handleSelectPromoAProductos = async (promo) => {
    const solicitudes = selectedItems.map(({ index, item }) => {
      const itemSubtotal = getItemSubtotal(item);
      let porcentaje = 0;
      if (promo.tipo === "PORCENTAJE") {
        porcentaje = Number(promo.valor) || 0;
      } else if (promo.tipo === "PRECIO") {
        const montoLimitado = Math.min(Number(promo.valor) || 0, itemSubtotal);
        porcentaje = itemSubtotal > 0 ? (montoLimitado / itemSubtotal) * 100 : 0;
      } else if (promo.tipo === "PROMOCION") {
        porcentaje = Number(promo.porcentaje_equivalente) || 0;
      }
      return { index, item, porcentaje: parseFloat(porcentaje.toFixed(2)) };
    });

    const aplicables = solicitudes.filter((s) => s.porcentaje > 0);
    if (aplicables.length === 0) {
      throw new Error("El porcentaje calculado de la promoción es 0 para los productos seleccionados.");
    }

    const resultados = await Promise.allSettled(
      aplicables.map(({ item, porcentaje }) =>
        postAplicarDescuentoPorArticulo({
          mesa,
          articuloId: item.id_producto || item.id,
          numeroCheque: chequeSeleccionado,
          descuentoId: promo.id,
          porcentaje,
          nombreDescuento: promo.nombre,
          empleado,
        })
      )
    );

    const fallos = resultados.filter((r) => r.status === "rejected");
    if (fallos.length > 0) {
      throw new Error(
        `No se pudo aplicar la promoción a ${fallos.length} de ${aplicables.length} producto(s) seleccionados.`
      );
    }

    const n = aplicables.length;
    return {
      message: `Promoción "${promo.nombre}" aplicada a ${n} producto${n > 1 ? "s" : ""} seleccionado${n > 1 ? "s" : ""}.`,
      // Para que App.js actualice el ticket de inmediato en "order" sin
      // depender de un refetch del cheque (mismo patrón que ya usa
      // "Descuentos": setOrder local primero, backend después — ver
      // handleApplyDiscount en App.js).
      perProductoUpdates: aplicables.map(({ index, porcentaje }) => ({
        index,
        discount: { id: promo.id, name: promo.nombre, price: porcentaje },
      })),
    };
  };

  // Comportamiento original, sin cambios: promo sobre el total del pedido.
  const handleSelectPromoATodoElPedido = async (promo) => {
    const montoCrudo = calcularMontoDescuento(promo);
    if (montoCrudo === null) {
      throw new Error(`Tipo de promoción no reconocido: ${promo.tipo}`);
    }

    const montoTotalDescuento = parseFloat(montoCrudo.toFixed(2));
    if (montoTotalDescuento <= 0) {
      throw new Error("El monto calculado de la promoción es 0.");
    }

    const data = await postAplicarPromo({
      mesa,
      numeroCheque: chequeSeleccionado,
      promoId: promo.id,
      nombrePromo: promo.nombre,
      tipoPromo: promo.tipo,
      montoTotalDescuento,
      empleado,
    });

    const monto = typeof data.montoAplicado === "number" ? data.montoAplicado.toFixed(2) : data.montoAplicado;
    return { message: `Promoción "${promo.nombre}" aplicada ($${monto})` };
  };

  const handleSelectPromo = async (promo) => {
    if (applying) return;
    if (!chequeSeleccionado) {
      setError("No hay cheque seleccionado.");
      return;
    }
    if (orderTotal <= 0) {
      setError(
        hasSelection
          ? "Los productos seleccionados no tienen precio válido."
          : "El pedido no tiene productos con precio válido."
      );
      return;
    }

    setApplying(true);
    setError(null);
    try {
      const resultado = hasSelection
        ? await handleSelectPromoAProductos(promo)
        : await handleSelectPromoATodoElPedido(promo);

      if (onApplyComplete) {
        onApplyComplete({
          success: true,
          message: resultado.message,
          // Presente solo cuando se aplicó por producto (hasSelection).
          // App.js lo usa para actualizar "order" localmente en vez de
          // recargar el cheque completo — ver nota en App.js.
          perProductoUpdates: resultado.perProductoUpdates || null,
        });
      }
      if (onClose) onClose();
    } catch (err) {
      console.error("[PromoSelector] Error:", err);
      setError(`⚠️ Error al aplicar promoción: ${err.message}`);
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="promo-selector">
      <div className="promo-selector-header">
        <h2><i className="bi bi-gift" style={{ marginRight: 8 }}></i>Promociones</h2>
        <button className="promo-close-button" onClick={onClose}>
          <FaTimes />
        </button>
      </div>

      <p className="promo-scope-info">
        {hasSelection
          ? `Se aplicará solo a ${selectedItems.length} producto${selectedItems.length > 1 ? "s" : ""} seleccionado${selectedItems.length > 1 ? "s" : ""} (subtotal: $${orderTotal.toFixed(2)}).`
          : `Se aplicará a todo el pedido (total: $${orderTotal.toFixed(2)}).`}
      </p>

      {error && <div className="promo-error">{error}</div>}
      {applying && <div className="promo-applying">Aplicando promoción...</div>}

      {availableTabs.length === 0 ? (
        <p className="promo-no-results">No hay promociones disponibles para este Revenue.</p>
      ) : (
        <>
          <div className="promo-tabs">
            {availableTabs.map((tab) => (
              <button
                key={tab}
                className={`promo-tab${currentTab === tab ? " active" : ""}`}
                onClick={() => setActiveTab(tab)}
                disabled={applying}
              >
                {TAB_LABELS[tab]}
              </button>
            ))}
          </div>

          <div className="promo-list">
            {promosVisibles
              .filter((p) => p.tipo === currentTab)
              .map((promo) => (
                <button
                  key={promo.id}
                  className="promo-item-button"
                  onClick={() => handleSelectPromo(promo)}
                  disabled={applying}
                >
                  {promo.nombre}
                  {promo.tipo === "PORCENTAJE" && ` (${promo.valor}%)`}
                  {promo.tipo === "PRECIO" && ` ($${Number(promo.valor).toFixed(2)})`}
                </button>
              ))}
          </div>
        </>
      )}

      <div className="promo-action-buttons">
        <button className="promo-cancel-button" onClick={onClose} disabled={applying}>
          Cerrar
        </button>
      </div>
    </div>
  );
};

PromoSelector.propTypes = {
  property: PropTypes.string,
  promos: PropTypes.array,
  order: PropTypes.array,
  selectedRevenue: PropTypes.object,
  selectedSpace: PropTypes.object,
  chequeSeleccionado: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  currentUser: PropTypes.object,
  selectedIndices: PropTypes.array,
  onApplyComplete: PropTypes.func,
  onClose: PropTypes.func.isRequired,
};

export default PromoSelector;
